/**
 * The browser end of a party room (docs/design/multiplayer.md §3, §5): one WebSocket to the
 * Room Durable Object, reconnecting with backoff while a key runs.
 *
 *   const party = new PartyClient(api.partySocketUrl(code), code);
 *   party.subscribe((e) => …);          // room / start / end / abort / error / status
 *   party.send({ t: 'ready', on: true });
 *   party.inbox                         // frames + snapshots for the running session (game/session.ts net)
 *
 * Frames are queued in `inbox` from the `start` message on, so nothing is lost while the run's
 * models load; the session drains it.
 */
import type { Command, World } from '@keystone/sim';
import { localDateString } from '../meta/forge/localDate';
import { type ClientMsg, type NetEnd, type NetFrame, type NetRunStart, type RoomView, type ServerMsg, NET_BUILD } from './protocol';

export type RunInboxItem = NetFrame | { t: 'snap'; k: number; world: World };

export type PartyEvent =
  | { type: 'status'; status: PartyClient['status'] }
  | { type: 'room'; room: RoomView }
  | { type: 'start'; run: NetRunStart }
  | { type: 'end'; end: NetEnd }
  | { type: 'abort'; reason: string }
  | { type: 'error'; code: string; msg: string };

const PING_MS = 2000;
const MAX_BACKOFF_MS = 8000;

export class PartyClient {
  status: 'connecting' | 'open' | 'closed' = 'connecting';
  room: RoomView | null = null;
  playerId: string | null = null;
  /** The run being played (null in the lobby). */
  run: NetRunStart | null = null;
  readonly inbox: RunInboxItem[] = [];
  /** Smoothed round trip (ms). */
  rttMs = 120;
  /** Lowest round trip of the last pings (ms): jitter only adds delay, so this is the link itself. */
  rttMinMs = 120;
  private rttSamples: number[] = [];

  private ws: WebSocket | null = null;
  private readonly listeners = new Set<(e: PartyEvent) => void>();
  private closedByUs = false;
  private retry = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pings = new Map<number, number>();
  private pingSeq = 0;
  private outbox: ClientMsg[] = [];
  /** `?netlag=ms` (round trip) / `?netjitter=ms`: artificial latency for testing prediction. */
  private readonly lagMs: number;
  private readonly jitterMs: number;
  private lastOutAt = 0;
  private lastInAt = 0;

  constructor(
    private readonly url: string,
    readonly code: string,
    lag: { lagMs?: number; jitterMs?: number } = lagFromUrl(),
  ) {
    this.lagMs = Math.max(0, lag.lagMs ?? 0);
    this.jitterMs = Math.max(0, lag.jitterMs ?? 0);
    this.open();
  }

  /** Run `fn` after half the artificial round trip (± jitter), never reordering one direction. */
  private delayed(dir: 'in' | 'out', fn: () => void): void {
    if (this.lagMs <= 0 && this.jitterMs <= 0) return fn();
    const t = performance.now();
    const d = this.lagMs / 2 + (Math.random() * 2 - 1) * (this.jitterMs / 2);
    const at = Math.max(t + Math.max(0, d), dir === 'in' ? this.lastInAt : this.lastOutAt);
    if (dir === 'in') this.lastInAt = at;
    else this.lastOutAt = at;
    setTimeout(fn, at - t);
  }

  subscribe(fn: (e: PartyEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  send(msg: ClientMsg): void {
    const ws = this.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const text = JSON.stringify(msg);
      this.delayed('out', () => {
        if (ws.readyState === WebSocket.OPEN) ws.send(text);
      });
    } else if (msg.t !== 'cmd' && msg.t !== 'ping') this.outbox.push(msg);
  }

  sendCommands(c: Command[]): void {
    if (c.length) this.send({ t: 'cmd', c });
  }

  /** Leave the party for good (no reconnect). */
  leave(): void {
    this.send({ t: 'leave' });
    this.close();
  }

  close(): void {
    this.closedByUs = true;
    this.stopPing();
    try {
      this.ws?.close(1000, 'bye');
    } catch {
      /* ignore */
    }
    this.ws = null;
    this.setStatus('closed');
  }

  private emit(e: PartyEvent): void {
    for (const fn of [...this.listeners]) {
      try {
        fn(e);
      } catch (err) {
        console.error('[party] listener threw', err);
      }
    }
  }

  private setStatus(s: PartyClient['status']): void {
    if (this.status === s) return;
    this.status = s;
    this.emit({ type: 'status', status: s });
  }

  private open(): void {
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      this.emit({ type: 'error', code: 'connect', msg: String(err) });
      this.setStatus('closed');
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setStatus('open');
      ws.send(JSON.stringify({ t: 'hello', build: NET_BUILD, localDate: localDateString() } satisfies ClientMsg));
      for (const m of this.outbox.splice(0)) ws.send(JSON.stringify(m));
      this.startPing();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data) as ServerMsg;
      } catch {
        return;
      }
      this.delayed('in', () => {
        if (this.ws === ws) this.onMessage(msg);
      });
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopPing();
      // 4000 = replaced by another tab, 4001 = refused (full, running, …): don't fight it.
      if (this.closedByUs || ev.code === 4000 || ev.code === 4001) {
        this.setStatus('closed');
        return;
      }
      this.setStatus('connecting');
      const wait = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.retry++);
      setTimeout(() => {
        if (!this.closedByUs) this.open();
      }, wait);
    };
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome':
        this.playerId = msg.playerId;
        return;
      case 'room':
        this.room = msg.room;
        this.emit({ type: 'room', room: msg.room });
        return;
      case 'start':
        // A fresh start (or the restart sent on reconnect) resets the run queue.
        this.inbox.length = 0;
        this.run = msg.run;
        this.emit({ type: 'start', run: msg.run });
        return;
      case 'snap':
        if (this.run) this.inbox.push(msg);
        return;
      case 'f':
        if (this.run) this.inbox.push({ ...msg, at: performance.now() });
        return;
      case 'end':
        this.run = null;
        this.emit({ type: 'end', end: msg });
        return;
      case 'abort':
        this.run = null;
        this.inbox.length = 0;
        this.emit({ type: 'abort', reason: msg.reason });
        return;
      case 'error':
        this.emit({ type: 'error', code: msg.code, msg: msg.msg });
        return;
      case 'pong': {
        const t0 = this.pings.get(msg.n);
        this.pings.delete(msg.n);
        if (t0 !== undefined) {
          const rtt = performance.now() - t0;
          this.rttMs = this.rttSamples.length ? this.rttMs * 0.7 + rtt * 0.3 : rtt;
          this.rttSamples = [...this.rttSamples, rtt].slice(-10);
          this.rttMinMs = Math.min(...this.rttSamples);
        }
        return;
      }
    }
  }

  private startPing(): void {
    this.stopPing();
    const ping = (): void => {
      const n = ++this.pingSeq;
      this.pings.set(n, performance.now());
      if (this.pings.size > 10) this.pings.delete(this.pings.keys().next().value!);
      this.send({ t: 'ping', n });
    };
    ping();
    this.pingTimer = setInterval(ping, PING_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
}

/** `?netlag=120&netjitter=20` (ms): test latency for party play (0 = off). */
export function lagFromUrl(): { lagMs: number; jitterMs: number } {
  try {
    const q = new URLSearchParams((globalThis as { location?: Location }).location?.search ?? '');
    const lagMs = Number(q.get('netlag') ?? 0) || 0;
    return { lagMs, jitterMs: Number(q.get('netjitter') ?? lagMs * 0.15) || 0 };
  } catch {
    return { lagMs: 0, jitterMs: 0 };
  }
}
